import React, { lazy, Suspense } from "react";
import { Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import AccessibilitySkipLink from "./components/AccessibilitySkipLink";
import RouteValidator from "./components/RouteValidator";
import { useGoogleAnalytics } from "./hooks/useGoogleAnalytics";
import { getAllRoutes } from "./constants/routes";

// Homepage stays eager — it's the most-hit entry point and must paint
// instantly. Everything else is route-lazy so each page chunk only loads
// when the user navigates to it (rather than being preloaded upfront).
import HomepageStrategicCommandCenter from "./pages/homepage-strategic-command-center";
import Enhanced404 from "./pages/Enhanced404";

// Admin pages — small, low-traffic, kept eager for now.
import Admin from "./pages/Admin";
import AdminAccess from "./components/AdminAccess";
import AdminTest from "./components/AdminTest";

// Lazy-loaded page components — each gets its own chunk via dynamic import.
const SuccessStoriesProvenTransformations = lazy(() => import("./pages/success-stories-proven-transformations"));
const MarketIntelligenceHubRealTimeInsights = lazy(() => import("./pages/market-intelligence-hub-real-time-insights"));
const GccSetupServicesComprehensiveMastery = lazy(() => import("./pages/gcc-setup-services-comprehensive-mastery"));
const GccSetupExecutiveEdition = lazy(() => import("./pages/gcc-setup-executive-edition"));
const RoiCalculatorHubInteractiveDecisionTools = lazy(() => import("./pages/roi-calculator-hub-interactive-decision-tools"));
const ResourcesInsightsKnowledgeAuthority = lazy(() => import("./pages/resources-insights-knowledge-authority"));
const IndividualInsightPage = lazy(() => import("./pages/resources-insights-knowledge-authority/components/IndividualInsightPage"));
const IndiaGccRegulatoryGuide = lazy(() => import("./pages/india-gcc-regulatory-guide"));
const Tier2GccCities2026 = lazy(() => import("./pages/tier-2-gcc-cities-2026"));
const EngagementModelsPartnershipFramework = lazy(() => import("./pages/engagement-models-partnership-framework"));
const GCCExpansionServices = lazy(() => import("./pages/gcc-expansion-services"));
const EngagementModelsFinder = lazy(() => import("./pages/engagement-models-finder"));
const ExecutiveHiringLeadershipSearch = lazy(() => import("./pages/executive-hiring-leadership-search"));
const SkillStackStaffingSolutions = lazy(() => import("./pages/skillstack-staffing-solutions"));
const AboutUsCompanyFoundationLeadership = lazy(() => import("./pages/about-us-company-foundation-leadership"));
const ContactPage = lazy(() => import("./pages/contact"));
const PrivacyPolicy = lazy(() => import("./pages/privacy-policy"));
const TermsOfService = lazy(() => import("./pages/terms-of-service"));
const CookiesPolicy = lazy(() => import("./pages/cookies-policy"));

// Component mapping for the auto-generated route loop. Keys must match
// the `component` field in src/constants/routes.js entries.
const componentMap = {
  HomepageStrategicCommandCenter,
  SuccessStoriesProvenTransformations,
  MarketIntelligenceHubRealTimeInsights,
  GccSetupServicesComprehensiveMastery,
  GccSetupExecutiveEdition,
  RoiCalculatorHubInteractiveDecisionTools,
  ResourcesInsightsKnowledgeAuthority,
  EngagementModelsPartnershipFramework,
  GCCExpansionServices,
  EngagementModelsFinder,
  ExecutiveHiringLeadershipSearch,
  SkillStackStaffingSolutions,
  AboutUsCompanyFoundationLeadership,
  ContactPage,
  Admin,
  AdminAccess,
  AdminTest,
  PrivacyPolicy,
  TermsOfService,
  CookiesPolicy,
  NotFound: Enhanced404
};

// Minimal route fallback shown briefly while a lazy chunk downloads.
// Kept simple so we don't introduce a layout shift between fallback and
// the real page.
const RouteFallback = () => (
  <div
    aria-busy="true"
    aria-live="polite"
    style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}
  >
    <span className="sr-only">Loading…</span>
  </div>
);

const Routes = () => {
  useGoogleAnalytics();

  const allRoutes = getAllRoutes();

  if (process.env.NODE_ENV === "development") {
    import("./constants/routes").then(({ checkRouteConsistency }) => {
      checkRouteConsistency();
    });
  }

  return (
    <ErrorBoundary>
      <RouteValidator>
        <div className="App">
          <AccessibilitySkipLink />
          <main id="main-content" tabIndex="-1">
            <ScrollToTop />
            <Suspense fallback={<RouteFallback />}>
              <RouterRoutes>
                {/* Admin routes — explicit, eager */}
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/dashboard" element={<Admin />} />
                <Route path="/admin-access" element={<AdminAccess />} />
                <Route path="/admin-test" element={<AdminTest />} />

                {/* Primary services */}
                <Route path="/services" element={<GccSetupExecutiveEdition />} />
                <Route path="/services/expansion" element={<GCCExpansionServices />} />
                <Route path="/services/staffing" element={<SkillStackStaffingSolutions />} />
                <Route path="/services/executive-hiring" element={<ExecutiveHiringLeadershipSearch />} />

                {/* Short canonical URLs */}
                <Route path="/case-studies" element={<SuccessStoriesProvenTransformations />} />
                <Route path="/calculator" element={<RoiCalculatorHubInteractiveDecisionTools />} />
                <Route path="/about" element={<AboutUsCompanyFoundationLeadership />} />
                <Route path="/team" element={<Navigate to="/about" replace />} />
                <Route path="/advisory-team-expert-credentials" element={<Navigate to="/about" replace />} />
                <Route path="/assessment" element={<EngagementModelsFinder />} />
                <Route path="/market-intelligence" element={<MarketIntelligenceHubRealTimeInsights />} />
                <Route path="/resources" element={<ResourcesInsightsKnowledgeAuthority />} />
                <Route path="/resources/india-gcc-regulatory-guide" element={<IndiaGccRegulatoryGuide />} />
                <Route path="/resources/tier-2-gcc-cities-2026" element={<Tier2GccCities2026 />} />

                {/* Homepage — eager-loaded entry point */}
                <Route path="/" element={<HomepageStrategicCommandCenter />} />

                {/* Legacy Executive Edition route */}
                <Route path="/gcc-setup-executive-edition" element={<GccSetupExecutiveEdition />} />
                <Route path="/gcc-setup-test" element={<Navigate to="/services" replace />} />
                <Route path="/gcc-setup-executive-edition-test" element={<Navigate to="/services" replace />} />

                {/* Automatically generate routes from configuration */}
                {allRoutes.map((route) => {
                  const Component = componentMap[route.component];

                  if (!Component) {
                    console.error(`Component ${route.component} not found for route ${route.path}`);
                    return null;
                  }

                  if (route.redirect) {
                    return (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={<Navigate to={route.redirect} replace />}
                      />
                    );
                  }

                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={<Component />}
                    />
                  );
                })}

                <Route
                  path="/resources-insights-knowledge-authority/insight/:id"
                  element={<IndividualInsightPage />}
                />

                <Route
                  path="/homepage-strategic-command-center"
                  element={<Navigate to="/" replace />}
                />

                <Route path="*" element={<Enhanced404 />} />
              </RouterRoutes>
            </Suspense>
          </main>
        </div>
      </RouteValidator>
    </ErrorBoundary>
  );
};

export default Routes;
